/**
 * US3/T017-T020 (FR-009 to FR-012, §19). Structure copied from the same precedent `tiers/New.tsx`
 * already established (017: `vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/
 * personal-info.tsx`'s `Form` + `FormFooter` shell, `ecommerce/category/create-category.tsx`'s
 * page-with-back-button + `FormGroup` layout) — this is a primary form the user lands on directly,
 * not a confirmation dialog for an action decided elsewhere, so `ReasonDialog` is not the right fit
 * here (that component gates an action already in flight, e.g. demote/cancel/reverse).
 *
 * `warehouseId` is never asked for or sent — `WarehouseOrDefault` picks the single v1 warehouse
 * server-side (FR-005/D-22). The SKU field is `SkuPicker` (ORD-03) — `listSkus({ q })` searching
 * code and name server-side, same as `Receipts.tsx`'s line rows.
 *
 * Only the data and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { useLoaderData, useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiSlidersHorizontalDuotone as AdjustmentIcon } from "react-icons/pi";
import { Alert, Text } from "rizzui";
import FormFooter from "@core/components/form-footer";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { can } from "../../lib/permissions";
import { formatMoment } from "../../lib/format";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { SectionTitle } from "../../components/section-title";
import type { AccountSummary, PermissionState } from "../../types";
import { createAdjustment, formatQty, listAdjustmentReasonCodes, reasonCodeLabel, skuLabel, type StockAdjustment } from "../../lib/services/warehouse";
import { SkuPicker } from "../../components/catalog-pickers";
import { useGoBack } from "../../lib/navigation";

export async function adjustmentsLoader() {
  const reasonCodes = await listAdjustmentReasonCodes();
  return { reasonCodes };
}

const adjustmentSchema = z.object({
  skuId: z.string().trim().uuid("Choose a SKU"),
  qtyDelta: z
    .string()
    .trim()
    .regex(/^-?\d+(\.\d+)?$/, "Enter a number")
    .refine((v) => Number(v) !== 0, "Enter a non-zero change — zero corrects nothing"),
  reasonCode: z.string().trim().min(1, "Required"),
  note: z.string().trim().optional(),
});
type AdjustmentFormValues = z.infer<typeof adjustmentSchema>;

const defaultValues: AdjustmentFormValues = { skuId: "", qtyDelta: "", reasonCode: "", note: "" };

export default function Adjustments() {
  const { reasonCodes } = useLoaderData() as Awaited<ReturnType<typeof adjustmentsLoader>>;
  const goBack = useGoBack("/inventory");
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [error, setError] = useState<unknown>(null);
  const [recorded, setRecorded] = useState<StockAdjustment | null>(null);
  const [resetValues, setResetValues] = useState<AdjustmentFormValues | null>(null);

  if (!can(permissionState, "inventory.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="adjust stock" />;
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="Adjust Stock"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Adjust" }]}
      />

      {/* FR-011: stated once, plainly — people behave differently when they know an action is logged. */}
      <Text className="max-w-2xl text-gray-500">
        Every adjustment is recorded permanently — SKU, quantity, reason, your name and the time —
        and cannot be edited or removed afterward.
      </Text>

      {recorded ? (
        <Alert color="success">
          Recorded: {formatQty(recorded.qtyDelta)} for SKU <span className="font-mono">{skuLabel(recorded)}</span>,
          reason &ldquo;{reasonCodeLabel(recorded.reasonCode)}&rdquo;, at {formatMoment(recorded.occurredAt)}.
        </Alert>
      ) : null}

      {error ? <ErrorSurface error={error} action="record this adjustment" /> : null}

      <Form<AdjustmentFormValues>
        validationSchema={adjustmentSchema}
        useFormProps={{ defaultValues }}
        resetValues={resetValues}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          try {
            const created = await createAdjustment({
              skuId: values.skuId,
              qtyDelta: Number(values.qtyDelta),
              reasonCode: values.reasonCode,
              note: values.note?.trim() || null,
            });
            setRecorded(created);
            toast.success("Adjustment recorded");
            setResetValues({ ...defaultValues });
          } catch (err) {
            setError(err);
            toast.error("Could not record the adjustment");
          }
        }}
      >
        {({ register, watch, setValue, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={AdjustmentIcon}>Adjustment</SectionTitle>} description="Corrects the on-hand quantity directly. Reserved stock is untouched.">
                <SkuPicker
                  value={watch("skuId")}
                  onChange={(id) => setValue("skuId", id, { shouldValidate: true })}
                  error={errors.skuId?.message}
                  required
                />
                <Field
                  label="Quantity change"
                  required
                  inputMode="decimal"
                  placeholder="e.g. 5 or -3"
                  hint="Positive adds stock, negative removes it, in the stocking unit."
                  error={errors.qtyDelta?.message}
                  {...register("qtyDelta")}
                />
                <SelectField
                  label="Reason"
                  required
                  disabled={reasonCodes.length === 0}
                  disabledReason={reasonCodes.length === 0 ? "No reason codes available." : undefined}
                  error={errors.reasonCode?.message}
                  {...register("reasonCode")}
                >
                  <option value="">{reasonCodes.length === 0 ? "No reason codes available" : "Select a reason"}</option>
                  {reasonCodes.map((code) => (
                    <option key={code} value={code}>
                      {reasonCodeLabel(code)}
                    </option>
                  ))}
                </SelectField>
                <Field label="Note (optional)" error={errors.note?.message} {...register("note")} />
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Recording…" : "Record adjustment"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
